/**
 * Which interface a request renders: the classic app or the Encuentro redesign.
 * Contract: docs/redesign/README.md §3 and docs/adr/003-interruptor-ui-version.md.
 *
 * Pure and framework-free, so the proxy, server code, client components and
 * tests share it.
 */

export const UI_VERSIONS = ['classic', 'encuentro'] as const
export type UiVersion = (typeof UI_VERSIONS)[number]

/**
 * The value every reader falls back to: missing row, bad value, DB error, a
 * document without the version stamp. Encuentro is the safe default (ALU-624):
 * it is what every member sees in production, so a failed read of
 * `platform_settings` must not repaint, re-mail or re-label as Classic.
 */
export const DEFAULT_UI_VERSION: UiVersion = 'encuentro'

export function isUiVersion(value: unknown): value is UiVersion {
  return typeof value === 'string' && (UI_VERSIONS as readonly string[]).includes(value)
}

/** Parse a raw cookie/query/jsonb value. Garbage becomes null, never a version. */
export function parseUiVersion(value: unknown): UiVersion | null {
  return isUiVersion(value) ? value : null
}

export interface ResolveUiVersionInput {
  /** `platform_settings.ui_version`, the single global switch. */
  global: UiVersion
  /** The `alumnia_ui_preview` cookie, already parsed. */
  previewCookie: UiVersion | null
  /** Whether this viewer may use the preview (see preview-access.ts). */
  canPreview: boolean
}

/** The cookie wins only for viewers allowed to preview; everyone else gets the global. */
export function resolveUiVersion({
  global,
  previewCookie,
  canPreview,
}: ResolveUiVersionInput): UiVersion {
  return canPreview && previewCookie ? previewCookie : global
}
